import { ReactNode, useState } from 'react';
import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';

import { SpinnerIconSVG } from 'src/assets/svg';
import { LoginStatusIndicator } from 'src/components/LoginStatusIndicator';
import container from 'src/container';
import {
    EventResponseStatus,
    EventResponseStatusRow,
    MunduEvent,
} from 'src/domain/event';
import {
    RESPONSE_STATUS_GROUPS,
    ResponseStatusGroup,
    getResponseStatusGroupCounts,
    getResponseStatusResidentLabel,
    sortResponseStatusRowsByResident,
    getUnansweredStatus,
    groupResponseStatusRows,
} from 'src/utils/eventResponseStatus';
import { getNameInitials } from 'src/utils/nameInitials';
import { getOriginalOccurrenceStart } from './eventFormModel';
import { useEventResponseStatus } from './useEventResponseStatus';

const {
    cradle: { munduClient },
} = container;

const formatResponseTime = (value: string, locale: string) =>
    new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(parseISO(value));

const GROUP_DOT_CLASS: Record<ResponseStatusGroup, string> = {
    going: 'bg-palmleafGreen',
    unanswered: 'bg-peachOrange',
    notGoing: 'bg-[#9A3B32]',
};

const StatusDot = ({ group }: { group: ResponseStatusGroup }) => (
    <span
        className={cx(
            'h-2.5 w-2.5 flex-shrink-0 rounded-full',
            GROUP_DOT_CLASS[group],
        )}
        aria-hidden="true"
    />
);

const GROUP_BADGE_CLASS: Record<ResponseStatusGroup, string> = {
    going: 'bg-[#E8F0DD] text-[#3E5A24]',
    unanswered: 'bg-peachOrange/30 text-woodyBrown',
    notGoing: 'bg-[#FBE7E5] text-[#9A3B32]',
};

const getRowGroup = (row: EventResponseStatusRow): ResponseStatusGroup =>
    row.status === 'going' || row.status === 'notGoing'
        ? row.status
        : 'unanswered';

const InviteeAvatar = ({ row }: { row: EventResponseStatusRow }) => {
    const [failedAvatarUrl, setFailedAvatarUrl] = useState<string>();

    return (
        <span className="relative flex h-10 w-10 flex-shrink-0">
            {row.avatarUrl && row.avatarUrl !== failedAvatarUrl ? (
                <img
                    src={row.avatarUrl}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                    onError={() => setFailedAvatarUrl(row.avatarUrl)}
                />
            ) : (
                <span
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-starkWhite text-sm font-semibold text-woodyBrown"
                    aria-hidden="true"
                >
                    {getNameInitials(row.name) || '?'}
                </span>
            )}
            <LoginStatusIndicator
                className="absolute bottom-0 right-0 ring-2 ring-vanillaWhite"
                hasEverLoggedIn={row.hasEverLoggedIn}
                lastLoggedInAt={row.lastLoggedInAt}
                size="avatar"
            />
        </span>
    );
};

const InviteeTableRow = ({ row }: { row: EventResponseStatusRow }) => {
    const { t } = useTranslation();
    const group = getRowGroup(row);
    const name = row.name ?? t('modules.calendar.responseStatus.unnamed');
    const residents = getResponseStatusResidentLabel(row);

    return (
        <tr data-status={group} className="align-top text-woodyBrown">
            <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                    <InviteeAvatar row={row} />
                    <div className="min-w-0 space-y-0.5">
                        <div className="break-words font-semibold">{name}</div>
                        {/* On narrow screens, show resident names beneath the invitee. */}
                        {residents ? (
                            <div className="text-sm text-mongooseGray md:hidden">
                                {t(
                                    'modules.calendar.responseStatus.relativeOf',
                                )}{' '}
                                {residents}
                            </div>
                        ) : null}
                    </div>
                </div>
            </td>
            <td className="hidden content-center break-words px-4 py-3 text-sm text-mongooseGray md:table-cell">
                {residents || '-'}
            </td>
            <td className="content-center px-4 py-3">
                <span
                    className={cx(
                        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold',
                        GROUP_BADGE_CLASS[group],
                    )}
                >
                    <StatusDot group={group} />
                    {t(`modules.calendar.responseStatus.${row.status}`)}
                </span>
            </td>
        </tr>
    );
};

/** Compact invitation total and reply progress, with reminder controls below. */
const ResponseStatusSummary = ({
    status,
    children,
}: {
    status: EventResponseStatus;
    children?: ReactNode;
}) => {
    const { t } = useTranslation();
    const groupCounts = getResponseStatusGroupCounts(status.counts);
    const total = status.counts.totalInvited;
    const barWidth = (count: number) =>
        total ? `${(count / total) * 100}%` : '0';

    return (
        <section className="space-y-3 rounded-2xl bg-white p-4 shadow-sm">
            <dl
                data-testid="response-status-counts"
                className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm"
            >
                <div className="flex items-center gap-2">
                    <dt className="text-woodyBrown/60">
                        {t('modules.calendar.responseStatus.totalInvited')}
                    </dt>
                    <dd className="font-semibold">{total}</dd>
                </div>
                <div className="flex items-center gap-3">
                    <dt className="sr-only">
                        {t('modules.calendar.responseStatus.respondedAt')}
                    </dt>
                    <dd className="font-semibold">
                        {t('modules.calendar.responseStatus.replyProgress', {
                            replied: groupCounts.going + groupCounts.notGoing,
                            total,
                        })}
                    </dd>
                    <div
                        className="flex h-2 w-20 overflow-hidden rounded-full bg-lightPeach"
                        aria-hidden="true"
                    >
                        <span
                            className="bg-palmleafGreen"
                            style={{ width: barWidth(groupCounts.going) }}
                        />
                        <span
                            className="bg-[#9A3B32]"
                            style={{ width: barWidth(groupCounts.notGoing) }}
                        />
                    </div>
                </div>
            </dl>
            {children ? (
                <div className="border-t border-woodyBrown/10 pt-3">
                    {children}
                </div>
            ) : null}
        </section>
    );
};

/** After the deadline the roster is frozen and no longer changes. */
const ResponseStatusClosedNotice = ({ deadline }: { deadline: string }) => {
    const { t, i18n } = useTranslation();

    return (
        <p role="status" className="rounded-2xl bg-white p-4 text-sm shadow-sm">
            {t('modules.calendar.responseStatus.closedNotice', {
                date: formatResponseTime(deadline, i18n.language),
            })}
        </p>
    );
};

type ResponseStatusFilter = 'all' | ResponseStatusGroup;

const RESPONSE_STATUS_FILTERS: ResponseStatusFilter[] = [
    'all',
    ...RESPONSE_STATUS_GROUPS,
];

/** One list of invitees, filtered by status, laid out like the relatives
 * table on a resident profile. */
const ResponseStatusTable = ({
    status,
    groupLabels,
}: {
    status: EventResponseStatus;
    groupLabels: Record<ResponseStatusGroup, string>;
}) => {
    const { t, i18n } = useTranslation();
    const [filter, setFilter] = useState<ResponseStatusFilter>('all');
    const groups = groupResponseStatusRows(status.rows);
    const rowsByFilter: Record<ResponseStatusFilter, EventResponseStatusRow[]> =
        {
            ...groups,
            all: RESPONSE_STATUS_GROUPS.flatMap(group => groups[group]),
        };
    const filterLabels: Record<ResponseStatusFilter, string> = {
        ...groupLabels,
        all: t('modules.calendar.responseStatus.all'),
    };
    const rows = sortResponseStatusRowsByResident(
        rowsByFilter[filter],
        i18n.language,
    );

    return (
        <section className="space-y-3">
            <div
                role="tablist"
                aria-label={t('modules.calendar.responseStatus.title')}
                className="inline-flex max-w-full flex-wrap gap-1 rounded-3xl bg-woodyBrown/5 p-1"
            >
                {RESPONSE_STATUS_FILTERS.map(option => (
                    <button
                        key={option}
                        type="button"
                        role="tab"
                        aria-selected={filter === option}
                        onClick={() => setFilter(option)}
                        className={cx(
                            'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
                            option === 'all'
                                ? filter === option
                                    ? 'bg-woodyBrown text-lightPeach'
                                    : 'text-woodyBrown/70 hover:bg-lightPeachMonoChrome hover:text-woodyBrown'
                                : [
                                      GROUP_BADGE_CLASS[option],
                                      'hover:brightness-95',
                                      filter === option &&
                                          'ring-1 ring-inset ring-current',
                                  ],
                        )}
                    >
                        {filterLabels[option]}
                        <span
                            className={cx(
                                'rounded-full px-1.5 text-xs',
                                option === 'all'
                                    ? filter === option
                                        ? 'bg-lightPeach/20'
                                        : 'bg-woodyBrown/10'
                                    : 'bg-white/50',
                            )}
                        >
                            {rowsByFilter[option].length}
                        </span>
                    </button>
                ))}
            </div>
            <div className="overflow-hidden rounded-xl border border-starkWhite bg-white">
                <table className="w-full table-auto text-woodyBrown md:table-fixed">
                    <thead className="text-sm">
                        <tr className="border-b border-starkWhite">
                            <th className="px-4 py-3 text-left font-semibold md:w-[45%]">
                                {t('common.name')}
                            </th>
                            <th className="hidden px-4 py-3 text-left font-semibold md:table-cell md:w-[30%]">
                                {t(
                                    'modules.calendar.responseStatus.relativeOf',
                                )}
                            </th>
                            <th className="px-4 py-3 text-left font-semibold md:w-[25%]">
                                {t(
                                    'modules.calendar.responseStatus.responseColumn',
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        data-testid="response-status-list"
                        className="divide-y divide-starkWhite"
                    >
                        {rows.length ? (
                            rows.map(row => (
                                <InviteeTableRow
                                    key={row.inviteeId}
                                    row={row}
                                />
                            ))
                        ) : (
                            <tr>
                                <td
                                    colSpan={3}
                                    className="px-4 py-6 text-center text-mongooseGray"
                                >
                                    {t('modules.calendar.responseStatus.empty')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

export const EventResponseStatusPanel = ({
    event,
    responseStatus,
}: {
    event: MunduEvent;
    responseStatus: ReturnType<typeof useEventResponseStatus>;
}) => {
    const { t } = useTranslation();
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderResult, setReminderResult] = useState<{
        sent: number;
        failed: number;
    }>();
    const [reminderError, setReminderError] = useState(false);
    const occurrenceStart = getOriginalOccurrenceStart(event);
    const { data, error, mutate } = responseStatus;
    const sendReminder = async (retryFailedOnly: boolean) => {
        const current = await mutate();
        if (!current) return;
        const count = retryFailedOnly
            ? current.retryEligibleCount
            : current.reminderEligibleCount;
        if (
            !count ||
            !window.confirm(
                t('modules.calendar.responseStatus.reminder.confirm', {
                    count,
                }),
            )
        )
            return;
        setSendingReminder(true);
        setReminderError(false);
        try {
            const result = await munduClient.sendEventResponseReminder(
                event.id,
                occurrenceStart,
                retryFailedOnly,
            );
            setReminderResult(result);
            await mutate();
        } catch (_error) {
            setReminderError(true);
        } finally {
            setSendingReminder(false);
        }
    };
    const groupLabels: Record<ResponseStatusGroup, string> = {
        going: t('modules.calendar.responseStatus.going'),
        unanswered: t(
            `modules.calendar.responseStatus.${getUnansweredStatus({
                closed: Boolean(data?.closed),
            })}`,
        ),
        notGoing: t('modules.calendar.responseStatus.notGoing'),
    };

    return (
        <div className="flex flex-1 flex-col">
            <div className="flex-1 px-5 py-5 sm:px-8 sm:py-6">
                <div className="mx-auto max-w-3xl space-y-4">
                    {error ? (
                        <p className="rounded-2xl bg-white p-5 text-sm">
                            {t('modules.calendar.responseStatus.loadError')}
                        </p>
                    ) : null}
                    {!data ? (
                        error ? null : (
                            <div className="flex justify-center py-8">
                                <SpinnerIconSVG className="h-8 w-8 animate-spin" />
                            </div>
                        )
                    ) : (
                        <>
                            {data.closed ? (
                                <ResponseStatusClosedNotice
                                    deadline={data.deadline}
                                />
                            ) : null}
                            <ResponseStatusSummary status={data}>
                                {data.canSendResponseReminder ||
                                data.reminderCounts.sent ||
                                data.reminderCounts.failed ||
                                reminderResult ||
                                reminderError ? (
                                    <div className="space-y-3">
                                        {data.canSendResponseReminder ? (
                                            <div className="flex flex-wrap gap-3">
                                                <button
                                                    type="button"
                                                    disabled={sendingReminder}
                                                    aria-label={t(
                                                        'modules.calendar.responseStatus.reminder.sendWithCount',
                                                        {
                                                            count: data.reminderEligibleCount,
                                                        },
                                                    )}
                                                    onClick={() =>
                                                        sendReminder(false)
                                                    }
                                                    className={cx(
                                                        'inline-flex items-center gap-3 rounded-full px-3 py-1.5 text-sm font-normal transition-colors hover:bg-peachOrange/40 disabled:opacity-50',
                                                        GROUP_BADGE_CLASS.unanswered,
                                                    )}
                                                >
                                                    <span
                                                        className="relative inline-flex h-5 w-5 flex-shrink-0 items-center justify-center"
                                                        aria-hidden="true"
                                                    >
                                                        <BellRing
                                                            className="h-4 w-4"
                                                            strokeWidth={1.8}
                                                        />
                                                        <span className="absolute -right-1.5 -top-1 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-peachOrange px-0.5 text-[9px] font-medium leading-none">
                                                            {
                                                                data.reminderEligibleCount
                                                            }
                                                        </span>
                                                    </span>
                                                    {t(
                                                        'modules.calendar.responseStatus.reminder.send',
                                                    )}
                                                </button>
                                                {data.canRetryFailedResponseReminders ? (
                                                    <button
                                                        type="button"
                                                        disabled={
                                                            sendingReminder
                                                        }
                                                        onClick={() =>
                                                            sendReminder(true)
                                                        }
                                                        className="rounded-full border border-woodyBrown px-5 py-2 text-sm font-semibold disabled:opacity-50"
                                                    >
                                                        {t(
                                                            'modules.calendar.responseStatus.reminder.retry',
                                                            {
                                                                count: data.retryEligibleCount,
                                                            },
                                                        )}
                                                    </button>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        {data.reminderCounts.sent ||
                                        data.reminderCounts.failed ? (
                                            <p className="text-sm text-woodyBrown/60">
                                                {t(
                                                    'modules.calendar.responseStatus.reminder.history',
                                                    data.reminderCounts,
                                                )}
                                            </p>
                                        ) : null}
                                        {reminderResult ? (
                                            <p
                                                role="status"
                                                className="text-sm"
                                            >
                                                {t(
                                                    'modules.calendar.responseStatus.reminder.result',
                                                    reminderResult,
                                                )}
                                            </p>
                                        ) : null}
                                        {reminderError ? (
                                            <p
                                                role="alert"
                                                className="text-sm text-venetianRed"
                                            >
                                                {t(
                                                    'modules.calendar.responseStatus.reminder.error',
                                                )}
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                            </ResponseStatusSummary>
                            <ResponseStatusTable
                                status={data}
                                groupLabels={groupLabels}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
